import * as React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/context/ToastContext';
import { connectedAccountsService } from '@/services/connectedAccountsService';
import { getErrorMessage } from '@/api/api';

interface UseStripeOnboardingReturn {
  handleStartMonetize: () => Promise<void>;
  isProcessing: boolean;
  stripeButtonLabel: string;
}

export function useStripeOnboarding(): UseStripeOnboardingReturn {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isProcessing, setIsProcessing] = React.useState(false);

  const isStripeActive = user?.stripe_account_status === 'active';

  const needsRefreshLink =
    user?.stripe_account_id &&
    (user.stripe_onboarding_completed === false || user.stripe_account_status === 'restricted');

  const handleStartMonetize = React.useCallback(async () => {
    setIsProcessing(true);
    try {
      if (needsRefreshLink) {
        const response = await connectedAccountsService.refreshLink();
        window.open(response.url, '_blank');
      } else if (isStripeActive || user?.stripe_account_id) {
        const response = await connectedAccountsService.getLoginLink();
        window.open(response.url, '_blank');
      } else {
        const response = await connectedAccountsService.create();
        window.open(response.onboarding_url, '_blank');
      }
    } catch (error) {
      addToast({
        title: 'Stripe Error',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [needsRefreshLink, isStripeActive, user?.stripe_account_id, addToast]);

  const stripeButtonLabel = isStripeActive
    ? 'STRIPE DASHBOARD'
    : user?.stripe_account_id && user.stripe_onboarding_completed === false
      ? 'CONTINUE SETUP'
      : user?.stripe_account_status === 'restricted'
        ? 'ACTION REQUIRED'
        : 'START MONETIZE';

  return { handleStartMonetize, isProcessing, stripeButtonLabel };
}
